import { cn } from '@/shared/lib/cn';
import { renderIcon, type IconLike } from './icon';
import { chipBackground, type ChipColor } from './lib/colors';
import { tv } from './lib/tv';

/** Root merge goes through `tv` so `className="rounded-8"` overrides the size radius (custom radius scale). */
const chipRoot = tv({ base: 'inline-flex shrink-0 items-center justify-center' });

export type IconChipSize = 20 | 24 | 26 | 28 | 32 | 36 | 40 | 44 | 48;

/** Size → radius / glyph (spec §1.7). */
const CHIP: Record<IconChipSize, { box: string; glyph: string }> = {
  20: { box: 'size-20 rounded-6', glyph: 'size-10' }, // active top-tab module chip
  24: { box: 'size-24 rounded-6', glyph: 'size-16' }, // sidebar group chips
  26: { box: 'size-26 rounded-7', glyph: 'size-14' }, // right-panel layer rows, rail "Chizma"
  28: { box: 'size-28 rounded-8', glyph: 'size-16' }, // layer rail, popover header
  32: { box: 'size-32 rounded-8', glyph: 'size-16' }, // side-sheet and modal-A headers
  36: { box: 'size-36 rounded-8', glyph: 'size-18' },
  40: { box: 'size-40 rounded-10', glyph: 'size-20' }, // canvas KPI widget
  44: { box: 'size-44 rounded-12', glyph: 'size-22' }, // modal-B header
  48: { box: 'size-48 rounded-14', glyph: 'size-24' }, // dashboard "Glass Card"
};

export interface IconChipProps {
  icon: IconLike;
  /** Named palette colour or any CSS colour (data). Default `accent`. */
  color?: ChipColor;
  size?: IconChipSize;
  /** Accessible label; decorative by default. */
  label?: string;
  className?: string;
}

/** Coloured rounded square with a white glyph (`muted`: White/10 + White/60 glyph). */
export function IconChip({ icon, color = 'accent', size = 28, label, className }: IconChipProps) {
  const bg = chipBackground(color);
  const spec = CHIP[size];
  return (
    <span
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={chipRoot({ class: [spec.box, bg.className, className] })}
      style={bg.style}
    >
      {renderIcon(icon, cn(spec.glyph, color === 'muted' ? 'text-icon' : 'text-white'))}
    </span>
  );
}

/** Fixed entity chips (spec §1.7 "Fixed chip map": element palette 319:9243, layer rail 384:7732, sidebar 471:7958). */
export const ENTITY_CHIPS = {
  chizma: { color: 'blue', icon: 'image-line' },
  soat: { color: 'darkBlue', icon: 'time-line' },
  sana: { color: 'darkRed', icon: 'calendar-line' },
  korsatkich: { color: 'pink', icon: 'dashboard-line' },
  sarlavha: { color: 'green', icon: 'text-block' },
  belgi: { color: 'yellow', icon: 'map-pin-line' },
  rasm: { color: 'purple', icon: 'image-line' },
  gif: { color: 'blue', icon: 'file-gif-line' },
  video: { color: 'orange', icon: 'play-line' },
  qurilmaMalumoti: { color: 'o5fcd3f', icon: 'information-line' },
  malumotlarBazasi: { color: 'o4a86f7', icon: 'bar-chart-2-line' },
  jadval: { color: 'oe4892f', icon: 'table-line' },
  qurilmaBoshqaruvi: { color: 'darkBlue', icon: 'rfid-fill' },
  xodimlar: { color: 'darkGreen', icon: 'team-line' },
  konnektorlar: { color: 'pink', icon: 'plug-line' },
} as const satisfies Record<string, { color: ChipColor; icon: string }>;
